import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import {
  ArrowBack, Sync, Search, CheckCircle, Error,
  HourglassEmpty, PlayArrow, FileCopy,
  NavigateNext, NavigateBefore,
} from "@material-ui/icons";
import fetchClient from "../../api/fetchClient";
import message from "../../helper/message";

const SYNC_PATH = "/stock/sync";

const STATUS_CONFIG = {
  PENDING: { label: "Aguardando",  color: "#f59e0b", bg: "#fef3c7", Icon: HourglassEmpty },
  RUNNING: { label: "Em execução", color: "#3b82f6", bg: "#dbeafe", Icon: PlayArrow      },
  SUCCESS: { label: "Concluído",   color: "#10b981", bg: "#d1fae5", Icon: CheckCircle    },
  ERROR:   { label: "Erro",        color: "#ef4444", bg: "#fee2e2", Icon: Error           },
};

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "PENDING",  label: "Aguardando" },
  { value: "RUNNING",  label: "Em execução" },
  { value: "SUCCESS",  label: "Concluído" },
  { value: "ERROR",    label: "Erro" },
];

function fmt(val) {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d) ? val : d.toLocaleString("pt-BR");
}

function SyncStatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#64748b", bg: "#f1f5f9", Icon: HourglassEmpty };
  const { label, color, bg, Icon } = cfg;
  return (
    <BadgeWrap style={{ background: bg, color }}>
      <Icon style={{ fontSize: 13, marginRight: 4 }} />
      {label}
    </BadgeWrap>
  );
}

export default function SincronizarEstoquePage() {
  const history = useHistory();

  // ── Aplicação IFS ─────────────────────────────────────────────────────────
  const [aplicacoes, setAplicacoes]   = useState([]);
  const [aplicacaoId, setAplicacaoId] = useState("");

  useEffect(() => {
    fetchClient.get("/ifs/getAllAplicacao").then(({ data }) => {
      const list = (Array.isArray(data) ? data : []).filter(a => a.id && !a.id.includes("#"));
      setAplicacoes(list);
      if (list.length > 0) setAplicacaoId(list[0].id);
    }).catch(() => {});
  }, []);

  // ── Sincronização ─────────────────────────────────────────────────────────
  const [syncing, setSyncing]           = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [lastJob, setLastJob]           = useState(null);

  const handleSync = async () => {
    if (!aplicacaoId) return message.error("Selecione uma aplicação IFS.");
    setSyncing(true);
    setLastJob(null);
    try {
      const { data } = await fetchClient.post(SYNC_PATH, { aplicacaoId });
      setLastJob(data);
      message.sucess("Sincronização solicitada com sucesso!");
    } catch (err) {
      if (err.response?.status === 429) {
        setLimitReached(true);
        message.error(err.response.data?.message || "Limite diário atingido (3 por dia).");
      } else {
        message.error(err.response?.data?.message || "Erro ao solicitar sincronização.");
      }
    } finally {
      setSyncing(false);
    }
  };

  // ── Listagem com paginação ────────────────────────────────────────────────
  const [filterDate, setFilterDate]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [pageSize, setPageSize]         = useState(20);
  const [listing, setListing]           = useState(false);
  const [listItems, setListItems]       = useState([]);
  const [listCount, setListCount]       = useState(null);
  const [nextKey, setNextKey]           = useState(null);
  const [cursorHistory, setCursorHistory] = useState([null]);
  const [pageIdx, setPageIdx]           = useState(0);
  const [searched, setSearched]         = useState(false);

  const fetchList = async (cursor = null) => {
    if (!aplicacaoId) return message.error("Selecione uma aplicação IFS.");
    setListing(true);
    try {
      const params = new URLSearchParams({ aplicacaoId, pageSize });
      if (filterDate)   params.set("businessDate", filterDate);
      if (filterStatus) params.set("status", filterStatus);
      if (cursor)       params.set("nextKey", cursor);
      const { data } = await fetchClient.get(`${SYNC_PATH}?${params}`);
      setListItems(data.items || []);
      setListCount(data.count ?? null);
      setNextKey(data.nextKey || null);
      setSearched(true);
    } catch (err) {
      message.error(err.response?.data?.message || "Erro ao listar jobs.");
    } finally {
      setListing(false);
    }
  };

  const handleListSearch = (e) => {
    e.preventDefault();
    setCursorHistory([null]);
    setPageIdx(0);
    fetchList(null);
  };

  const goNextPage = () => {
    const newHistory = [...cursorHistory.slice(0, pageIdx + 1), nextKey];
    setCursorHistory(newHistory);
    setPageIdx(pageIdx + 1);
    fetchList(nextKey);
  };

  const goPrevPage = () => {
    const prevCursor = cursorHistory[pageIdx - 1] ?? null;
    setPageIdx(pageIdx - 1);
    fetchList(prevCursor);
  };

  const hasPrev = pageIdx > 0;
  const hasNext = !!nextKey;

  const handleAppChange = (e) => {
    setAplicacaoId(e.target.value);
    setLimitReached(false);
    setLastJob(null);
    setListItems([]);
    setListCount(null);
    setSearched(false);
    setCursorHistory([null]);
    setPageIdx(0);
  };

  return (
    <Page>
      <Header>
        <HeaderLeft>
          <BackBtn type="button" onClick={() => history.goBack()}>
            <ArrowBack style={{ fontSize: 20 }} />
          </BackBtn>
          <HeaderIcon src="/image/sync.png" alt="Sincronizar Estoque" />
          <HeaderText>
            <HeaderTitle>Sincronização Manual de Estoque</HeaderTitle>
            <HeaderSub>IFS — solicite uma sincronização imediata ou consulte o histórico de jobs</HeaderSub>
          </HeaderText>
        </HeaderLeft>
      </Header>

      {/* ── Card de Ação ── */}
      <ActionCard>
        <ActionCardBody>
          <AppBlock>
            <FieldLabel>Aplicação IFS *</FieldLabel>
            <AppSelect value={aplicacaoId} onChange={handleAppChange}>
              <option value="">— Selecione a aplicação —</option>
              {aplicacoes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </AppSelect>
          </AppBlock>

          <SyncBlock>
            {limitReached ? (
              <LimitBanner>
                <Error style={{ fontSize: 18, marginRight: 8 }} />
                Limite diário atingido (3 por dia). Tente novamente amanhã.
              </LimitBanner>
            ) : (
              <SyncBtn type="button" onClick={handleSync} disabled={syncing || !aplicacaoId}>
                <SpinIcon $spinning={syncing}><Sync style={{ fontSize: 18 }} /></SpinIcon>
                {syncing ? "Solicitando…" : "Sincronizar Estoque"}
              </SyncBtn>
            )}
          </SyncBlock>
        </ActionCardBody>
        {!limitReached && <SyncNote>Máximo de 3 solicitações manuais por dia</SyncNote>}

        {lastJob && (
          <JobResultBanner>
            <JobResultHeader>
              <CheckCircle style={{ fontSize: 16, marginRight: 6, color: "#10b981" }} />
              Sincronização solicitada — acompanhe o status no histórico abaixo
            </JobResultHeader>
            <JobResultGrid>
              <JobResultRow>
                <JobResultLabel>Job ID</JobResultLabel>
                <JobResultValueRow>
                  <JobResultValue mono>{lastJob.jobId}</JobResultValue>
                  <CopyBtn type="button" onClick={() => { navigator.clipboard.writeText(lastJob.jobId); message.sucess("Copiado!"); }}>
                    <FileCopy style={{ fontSize: 12 }} />
                  </CopyBtn>
                </JobResultValueRow>
              </JobResultRow>
              <JobResultRow>
                <JobResultLabel>Status</JobResultLabel>
                <SyncStatusBadge status={lastJob.status} />
              </JobResultRow>
              {lastJob.businessDate && (
                <JobResultRow>
                  <JobResultLabel>Data de Negócio</JobResultLabel>
                  <JobResultValue>{lastJob.businessDate}</JobResultValue>
                </JobResultRow>
              )}
              <JobResultRow>
                <JobResultLabel>Criado em</JobResultLabel>
                <JobResultValue>{fmt(lastJob.createdAt)}</JobResultValue>
              </JobResultRow>
            </JobResultGrid>
          </JobResultBanner>
        )}
      </ActionCard>

      {/* ── Histórico de Jobs ── */}
      <HistoryCard>
        <HistoryCardHeader>
          <HistoryTitle>Histórico de Jobs</HistoryTitle>
          <HistorySub>Consulte o histórico de sincronizações com filtros por data e status</HistorySub>
        </HistoryCardHeader>

        <form onSubmit={handleListSearch}>
          <FilterGrid>
            <FilterGroup>
              <FieldLabel>Data de Negócio</FieldLabel>
              <FilterInput
                type="date"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <FieldLabel>Status</FieldLabel>
              <FilterSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FieldLabel>Registros por página</FieldLabel>
              <FilterSelect value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </FilterSelect>
            </FilterGroup>
            <FilterGroup style={{ justifyContent: "flex-end" }}>
              <FieldLabel>&nbsp;</FieldLabel>
              <SearchBtn type="submit" disabled={listing || !aplicacaoId}>
                <Search style={{ fontSize: 16, marginRight: 6 }} />
                {listing ? "Buscando…" : "Buscar"}
              </SearchBtn>
            </FilterGroup>
          </FilterGrid>
        </form>

        {listItems.length > 0 && (
          <>
            <ListMeta>
              {listCount != null && <span><strong>{listCount}</strong> job(s) encontrado(s)</span>}
            </ListMeta>

            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Job ID</Th>
                    <Th>Status</Th>
                    <Th>Data de Negócio</Th>
                    <Th>Criado em</Th>
                    <Th>Finalizado em</Th>
                    <Th>Erro</Th>
                  </tr>
                </thead>
                <tbody>
                  {listItems.map((job, i) => (
                    <Tr key={job.jobId || i} $isNew={lastJob && job.jobId === lastJob.jobId}>
                      <Td mono title={job.jobId}>
                        <JobIdCell>
                          <span>{job.jobId}</span>
                          <CopyBtn type="button" onClick={() => { navigator.clipboard.writeText(job.jobId); message.sucess("Copiado!"); }}>
                            <FileCopy style={{ fontSize: 12 }} />
                          </CopyBtn>
                        </JobIdCell>
                      </Td>
                      <Td><SyncStatusBadge status={job.status} /></Td>
                      <Td>{job.businessDate || "—"}</Td>
                      <Td>{fmt(job.createdAt)}</Td>
                      <Td>{fmt(job.finishedAt)}</Td>
                      <Td>
                        {job.error
                          ? <ErrorInline title={job.error}>{job.error.slice(0, 60)}{job.error.length > 60 ? "…" : ""}</ErrorInline>
                          : "—"}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>

            <PaginationRow>
              <PagBtn type="button" onClick={goPrevPage} disabled={!hasPrev || listing}>
                <NavigateBefore style={{ fontSize: 18 }} /> Anterior
              </PagBtn>
              <PagInfo>Página {pageIdx + 1}</PagInfo>
              <PagBtn type="button" onClick={goNextPage} disabled={!hasNext || listing}>
                Próxima <NavigateNext style={{ fontSize: 18 }} />
              </PagBtn>
            </PaginationRow>
          </>
        )}

        {!listing && searched && listItems.length === 0 && (
          <EmptyList>Nenhum job encontrado para os filtros selecionados.</EmptyList>
        )}

        {!searched && !listing && (
          <EmptyList>Use os filtros acima e clique em <strong>Buscar</strong> para consultar o histórico.</EmptyList>
        )}
      </HistoryCard>
    </Page>
  );
}

// ── Styled Components ────────────────────────────────────────────────────────

const spin   = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height:100vh;background:#f8fafc;padding:32px;font-family:'Mulish',sans-serif;
`;
const Header     = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:28px;`;
const HeaderLeft = styled.div`display:flex;align-items:center;gap:16px;`;
const BackBtn    = styled.button`
  background:white;border:1px solid #e2e8f0;border-radius:10px;
  width:40px;height:40px;display:flex;align-items:center;justify-content:center;cursor:pointer;
  &:hover{background:#f1f5f9;}
`;
const HeaderIcon  = styled.img`width:40px;height:40px;object-fit:contain;`;
const HeaderText  = styled.div``;
const HeaderTitle = styled.h1`margin:0;font-size:22px;font-weight:700;color:#1e293b;`;
const HeaderSub   = styled.p`margin:0;font-size:13px;color:#64748b;`;

// ── Action Card ──────────────────────────────────────────────────────────────
const ActionCard = styled.div`
  background:white;border-radius:16px;padding:24px 28px;
  box-shadow:0 2px 8px rgba(0,0,0,.06);border:1px solid #e8e8e8;
  margin-bottom:20px;animation:${fadeIn} .4s ease;
`;
const ActionCardBody = styled.div`
  display:flex;align-items:flex-end;gap:24px;flex-wrap:wrap;
`;
const AppBlock = styled.div`
  display:flex;flex-direction:column;gap:6px;flex:1;min-width:240px;
`;
const SyncBlock = styled.div`display:flex;align-items:center;`;
const SyncNote  = styled.span`
  display:block;margin-top:6px;font-size:11px;color:#94a3b8;
`;

const FieldLabel = styled.label`font-size:12px;font-weight:700;color:#64748b;`;
const AppSelect  = styled.select`
  width:100%;padding:10px 14px;border:1px solid #e2e8f0;border-radius:10px;
  font-size:14px;font-family:'Mulish',sans-serif;outline:none;color:#1e293b;
  &:focus{border-color:#ff6b35;box-shadow:0 0 0 3px rgba(255,107,53,.1);}
`;

const SpinIcon = styled.span`
  display:inline-flex;align-items:center;margin-right:8px;
  animation:${p => p.$spinning ? css`${spin} 1s linear infinite` : "none"};
`;
const SyncBtn = styled.button`
  display:inline-flex;align-items:center;
  background:linear-gradient(135deg,#ff6b35 0%,#e85a2a 100%);
  color:white;border:none;border-radius:10px;padding:12px 28px;
  font-size:15px;font-weight:700;cursor:pointer;
  box-shadow:0 4px 12px rgba(255,107,53,.35);transition:all .2s;
  &:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 6px 18px rgba(255,107,53,.45);}
  &:active:not(:disabled){transform:translateY(0);}
  &:disabled{background:#bdbdbd;cursor:not-allowed;box-shadow:none;}
`;
const LimitBanner = styled.div`
  display:inline-flex;align-items:center;
  background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;
  border-radius:10px;padding:12px 20px;font-size:14px;font-weight:600;
`;

// Job result banner (inside ActionCard after sync)
const JobResultBanner = styled.div`
  margin-top:20px;border-radius:12px;border:1px solid #a7f3d0;background:#f0fdf4;
  overflow:hidden;animation:${fadeIn} .3s ease;
`;
const JobResultHeader = styled.div`
  display:flex;align-items:center;padding:10px 16px;
  background:#d1fae5;font-size:13px;font-weight:700;color:#065f46;
  border-bottom:1px solid #a7f3d0;
`;
const JobResultGrid      = styled.div`display:flex;flex-wrap:wrap;gap:0;`;
const JobResultRow       = styled.div`display:flex;align-items:center;gap:10px;padding:8px 16px;border-right:1px solid #d1fae5;`;
const JobResultLabel     = styled.span`font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;margin-right:4px;`;
const JobResultValueRow  = styled.div`display:flex;align-items:center;gap:6px;`;
const JobResultValue     = styled.span`font-size:13px;color:#065f46;font-weight:600;${p => p.mono && "font-family:'Courier New',monospace;font-size:12px;"}`;
const CopyBtn            = styled.button`background:none;border:1px solid #a7f3d0;border-radius:6px;padding:3px 6px;cursor:pointer;color:#10b981;display:inline-flex;align-items:center;&:hover{background:#d1fae5;}`;

// ── History Card ─────────────────────────────────────────────────────────────
const HistoryCard = styled.div`
  background:white;border-radius:16px;padding:28px;
  box-shadow:0 2px 8px rgba(0,0,0,.06);border:1px solid #e8e8e8;
  animation:${fadeIn} .4s ease;
`;
const HistoryCardHeader = styled.div`margin-bottom:20px;padding-bottom:16px;border-bottom:2px solid #f0f0f0;`;
const HistoryTitle = styled.h2`margin:0;font-size:17px;font-weight:700;color:#1e293b;`;
const HistorySub   = styled.p`margin:4px 0 0;font-size:13px;color:#64748b;`;

const FilterGrid   = styled.div`display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end;margin-bottom:4px;`;
const FilterGroup  = styled.div`display:flex;flex-direction:column;gap:4px;min-width:150px;flex:1;`;
const FilterInput  = styled.input`
  padding:10px 14px;border:1px solid #e2e8f0;border-radius:10px;
  font-size:14px;font-family:'Mulish',sans-serif;outline:none;
  &:focus{border-color:#ff6b35;box-shadow:0 0 0 3px rgba(255,107,53,.1);}
`;
const FilterSelect = styled.select`
  padding:10px 12px;border:1px solid #e2e8f0;border-radius:10px;
  font-size:14px;font-family:'Mulish',sans-serif;outline:none;background:white;
  &:focus{border-color:#ff6b35;}
`;
const SearchBtn = styled.button`
  display:inline-flex;align-items:center;
  background:#1e293b;color:white;border:none;border-radius:10px;
  padding:10px 22px;font-size:14px;font-weight:700;cursor:pointer;white-space:nowrap;
  &:hover:not(:disabled){background:#334155;}
  &:disabled{opacity:.5;cursor:not-allowed;}
`;

// Badge
const BadgeWrap = styled.span`display:inline-flex;align-items:center;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:700;`;

// Listagem
const ListMeta     = styled.div`margin:16px 0 8px;font-size:13px;color:#64748b;`;
const TableWrapper = styled.div`overflow-x:auto;border:1px solid #e2e8f0;border-radius:12px;`;
const Table        = styled.table`width:100%;border-collapse:collapse;font-size:13px;`;
const Th           = styled.th`padding:11px 14px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#64748b;background:#f8fafc;border-bottom:2px solid #e2e8f0;white-space:nowrap;`;
const Tr           = styled.tr`
  border-bottom:1px solid #f1f5f9;
  &:last-child{border-bottom:none;}
  &:hover{background:#fafbfc;}
  ${p => p.$isNew && "background:#f0fdf4 !important;"}
`;
const Td           = styled.td`padding:11px 14px;color:#334155;vertical-align:middle;${p => p.mono && "font-family:'Courier New',monospace;font-size:12px;"}`;
const JobIdCell    = styled.div`display:flex;align-items:center;gap:6px;`;
const ErrorInline  = styled.span`color:#ef4444;font-size:12px;cursor:help;`;
const EmptyList    = styled.div`padding:32px;text-align:center;color:#94a3b8;font-size:14px;`;

// Paginação
const PaginationRow = styled.div`display:flex;align-items:center;justify-content:center;gap:16px;margin-top:16px;padding-top:14px;border-top:1px solid #f1f5f9;`;
const PagBtn  = styled.button`display:inline-flex;align-items:center;gap:2px;background:white;border:1px solid #e2e8f0;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;color:#334155;&:hover:not(:disabled){border-color:#1e293b;color:#1e293b;}&:disabled{opacity:.4;cursor:not-allowed;}`;
const PagInfo = styled.span`font-size:13px;color:#64748b;`;
